import { QueryClient } from '@tanstack/react-query';
import { isAppError, isNonRetryable } from '@/lib/api/errors';

/**
 * QueryClient with Almanac-tuned defaults (plan §4.4 / §7).
 * - Don't retry client-fault errors (validation/forbidden/404/conflict).
 * - Retry transient server/network errors twice.
 * - Reasonable staleness; per-query overrides tune schemas/settings (long) vs lists (short).
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: (failureCount, error) => {
          if (isNonRetryable(error)) return false;
          // Never retry a 401 here — the client interceptor already handled refresh-retry.
          if (isAppError(error) && error.is('unauthorized')) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
        // Attempt the write even when the browser reports itself offline. TanStack's
        // default (`networkMode: 'online'`) PAUSES a mutation while `navigator.onLine` is
        // false: no request, no error, `isPending` stays true — a save button spinning
        // forever. Every write here has an onError that explains itself, so firing and
        // failing honestly (as a `network` AppError) is the behaviour the screens are
        // written for. `navigator.onLine` is a poor oracle anyway.
        networkMode: 'always',
      },
    },
  });
}
